---
title: "An interview won't auto-schedule"
description: "The AI scheduler in Chosen offered no times or seems stuck. The usual causes — missing availability, no calendar, tight windows, hard constraints — and the fixes."
category: "troubleshooting"
keywords:
  - "scheduling not working"
  - "AI scheduler no times"
  - "interview won't schedule"
  - "no slots offered"
  - "fix interview scheduling"
schema: "HowTo"
nav:
  section: "troubleshooting"
  order: 20
  parent: "/troubleshooting"
---

You started a scheduling request and nothing happened — no times offered, no email sent, the request just sitting there. The [AI scheduler](/guides/scheduling/ai-scheduler-overview) doesn't guess past a problem; when it can't find a slot, it stops and waits. That's by design, and it means the cause is usually one of the things below. Work down the list in order.

## Not everyone has submitted availability

If you're using the [candidate scheduling link](/guides/scheduling/candidate-scheduling-link), the scheduler waits until **every required participant** has submitted their availability before it does anything. One required link still sitting on **Pending** holds up the whole request.

**Check it:** open the request and look at each participant's link state — **Pending**, **Opened**, or **Submitted**.

**Fix it:**

<Steps>
  <li>Find the participant whose link is still **Pending** or **Opened**.</li>
  <li>If the link is old, re-send it from the candidate's detail page — tokenized links are time-limited and an expired one won't open.</li>
  <li>Nudge whoever hasn't submitted. Optional interviewers don't block the request, so it's a required one to chase.</li>
</Steps>

## A calendar isn't connected

The scheduler reads [Google Calendar](/getting-started/integrations/google-calendar) free/busy to find real openings. An interviewer with no connected calendar is invisible to it — and without any calendar in the loop, the scheduler can't find automatic times at all.

**Check it:** every interviewer on the loop should have Google Calendar connected under **Settings → Integrations**. Remember the integration is per-person — your connection doesn't connect theirs.

**Fix it:** have each interviewer connect their own calendar. Until they do, you can still schedule by hand — pick the times yourself — it's just more work. If a calendar shows disconnected when it shouldn't, see [integration issues](/troubleshooting/integration-issues).

## Availability windows are too tight or empty

[Availability windows](/guides/scheduling/availability-windows) are the envelope the scheduler searches inside — it never offers a time outside someone's window. A window narrowed to a couple of hours, against a busy calendar, can leave zero room.

**Check it:** under **Settings → Scheduling → Availability**, look at each interviewer's window. A tight window plus a full calendar over the next five business days is a common no-slot cause.

**Fix it:**

<Steps>
  <li>Ask interviewers with very narrow windows to widen them to the hours they'd genuinely take an interview.</li>
  <li>Check for blacklisted recurring slots that may be eating the only free time.</li>
  <li>Re-run the scheduling request — the scheduler uses the updated window immediately.</li>
</Steps>

## The scheduling automation was stopped

Every scheduling request runs in an automation mode. If a request was switched to **Off** — via **Stop scheduling** in the [AI events tab](/guides/scheduling/ai-events-tab) — the AI halts entirely: no drafting, no sending, no reading replies. A stopped request looks identical to a stuck one.

**Check it:** open the request and check its automation mode. **Off** means the AI isn't going to act.

**Fix it:** if you stopped the request and want the AI back on it, switch the mode back on, or start a fresh scheduling request from the candidate's detail page.

## Hard natural-language constraints ruled out every slot

A [natural-language constraint](/guides/scheduling/nl-constraints) phrased as a hard limit — "only Monday mornings", "must be before 10am" — is treated as a wall. If that wall lines up with no free calendar time, the scheduler finds nothing and stops.

**Check it:** look at the preferences you typed into the request. Words like "only" and "must" read as hard limits; "prefer" and "ideally" read as soft.

**Fix it:** re-run the request with the constraint loosened — swap "only mornings" for "prefer mornings" so the scheduler can fall back to an afternoon when mornings are full.

## The email thread aged out

The [AI email assistant](/guides/scheduling/email-assistant) watches a scheduling thread for about 14 days, plus a buffer after the meeting, then stops. A candidate reply that lands on a long-dead thread won't restart anything.

**Check it:** if the conversation has been quiet for weeks and a late reply isn't being picked up, the monitoring window has closed.

**Fix it:** start a fresh scheduling request from the candidate's detail page. A new request gives the assistant a live thread to work.

<Callout type="warning" title="Replying yourself tells the AI to back off">
A reply from you on a scheduling thread is the signal that you've taken over — the assistant stops drafting. If scheduling went quiet right after you sent a quick "thanks!", that's why. To leave a note without stopping the AI, use the **Comment** action on the thread inside Chosen instead.
</Callout>

## Still stuck?

If everyone has a connected calendar, windows are open, and no hard constraint is in the way but the scheduler still offers nothing, open **Settings → Contact support** with the candidate's name and the scheduling request so we can look at the overlap math.
